import { supabase } from '../supabase';

export interface Receiver {
  id: number;
  name: string;
  country: string;
  alternative_names: string[];
  created_at: string;
  receiver_formats?: ReceiverFormat[];
}

export interface ReceiverFormat {
  id: number;
  name: string;
  receiver_id: number;
  image_url: string | null;
  created_at: string;
}

// ── Receivers ──

export async function getReceivers(): Promise<Receiver[]> {
  const { data, error } = await supabase
    .from('receivers')
    .select('*, receiver_formats(*)')
    .order('name');

  if (error) {
    console.error('Error fetching receivers:', error);
    return [];
  }

  return data;
}

export async function createReceiver(
  receiver: Pick<Receiver, 'name' | 'country' | 'alternative_names'>
): Promise<Receiver | null> {
  const { data, error } = await supabase
    .from('receivers')
    .insert([receiver])
    .select()
    .single();

  if (error) {
    console.error('Error creating receiver:', error);
    return null;
  }

  return data;
}

export async function updateReceiver(
  id: number,
  receiver: Partial<Pick<Receiver, 'name' | 'country' | 'alternative_names'>>
): Promise<Receiver | null> {
  const { data, error } = await supabase
    .from('receivers')
    .update(receiver)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating receiver:', error);
    return null;
  }

  return data;
}

export async function deleteReceiver(id: number): Promise<boolean> {
  const { error } = await supabase.from('receivers').delete().eq('id', id);

  if (error) {
    console.error('Error deleting receiver:', error);
    return false;
  }

  return true;
}

// ── Receiver Formats ──

export async function getReceiverFormats(
  receiverId: number
): Promise<ReceiverFormat[]> {
  const { data, error } = await supabase
    .from('receiver_formats')
    .select('*')
    .eq('receiver_id', receiverId)
    .order('name');

  if (error) {
    console.error('Error fetching receiver formats:', error);
    return [];
  }

  return data;
}

export async function createReceiverFormat(
  format: Pick<ReceiverFormat, 'name' | 'receiver_id' | 'image_url'>
): Promise<ReceiverFormat | null> {
  const { data, error } = await supabase
    .from('receiver_formats')
    .insert([format])
    .select()
    .single();

  if (error) {
    console.error('Error creating receiver format:', error);
    return null;
  }

  return data;
}

export async function updateReceiverFormat(
  id: number,
  format: Partial<Pick<ReceiverFormat, 'name' | 'image_url'>>
): Promise<ReceiverFormat | null> {
  const { data, error } = await supabase
    .from('receiver_formats')
    .update(format)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating receiver format:', error);
    return null;
  }

  return data;
}

export async function deleteReceiverFormat(id: number): Promise<boolean> {
  const { error } = await supabase
    .from('receiver_formats')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting receiver format:', error);
    return false;
  }

  return true;
}
